import React, { useCallback, useEffect, useState } from 'react';
import {
  createBitrixOpenLine,
  getBitrixBindings,
  getBitrixOpenLines,
  getBitrixOperatorRouting,
  getBitrixState,
  getBitrixUsers,
  saveBitrixBindings,
  saveBitrixOperatorRouting,
  saveBitrixConfig,
  startBitrixOAuth,
} from '../api.js';
import CompactOperatorAccess from './CompactOperatorAccess.jsx';

const TYPE_LABELS = {
  telegram_personal: 'Telegram — личный',
  telegram_bot: 'Telegram — бот',
  whatsapp_business: 'WhatsApp Business',
  whatsapp_personal: 'WhatsApp — личный',
  max_personal: 'MAX — личный',
  max_bot: 'MAX — бот',
};

function channelLabel(channel) {
  return (
    TYPE_LABELS[channel.channel_type]
    || channel.channel_type
    || 'Мессенджер'
  );
}

export default function Bitrix() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [creatingLine, setCreatingLine] = useState(false);

  const [state, setState] = useState(null);
  const [portal, setPortal] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [lines, setLines] = useState([]);
  const [channels, setChannels] = useState([]);
  const [routingItems, setRoutingItems] = useState([]);
  const [routingDraft, setRoutingDraft] = useState({});
  const [routingSaving, setRoutingSaving] = useState(false);
  const [bitrixEmployees, setBitrixEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState('');
  const [newLineName, setNewLineName] = useState('');
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);

    try {
      const connection = await getBitrixState();

      setState(connection);

      setPortal(connection.portal_domain || '');
      setClientId(connection.client_id || '');
      setClientSecret('');

      if (!connection.connected) {
        setLines([]);
        setChannels([]);
        setRoutingItems([]);
        setRoutingDraft({});
        return;
      }

      const [
        lineResult,
        bindingResult,
        routingResult,
        usersResult,
      ] = await Promise.all([
        getBitrixOpenLines(),
        getBitrixBindings(),
        getBitrixOperatorRouting(),
        getBitrixUsers().catch((error) => ({
          employeeLoadError: error.message || 'Ошибка загрузки сотрудников',
        })),
      ]);

      const employeeList = Array.isArray(usersResult)
        ? usersResult
        : (usersResult?.users || usersResult?.items || []);

      setBitrixEmployees(
        Array.isArray(employeeList) ? employeeList : [],
      );
      setEmployeesError(usersResult?.employeeLoadError || '');
      setEmployeesLoading(false);

      const loadedRouting =
        routingResult.items || [];

      setLines(lineResult.lines || []);
      setChannels(bindingResult.channels || []);
      setRoutingItems(loadedRouting);
      setRoutingDraft(
        Object.fromEntries(
          loadedRouting.map((item) => [
            item.channel_id,
            (item.operator_external_ids || []).join(', '),
          ]),
        ),
      );
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.message || 'Не удалось загрузить интеграцию Bitrix24',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('bitrix') === 'connected') {
      setMessage({
        type: 'success',
        text: 'Bitrix24 успешно авторизован.',
      });

      params.delete('bitrix');

      const next = params.toString();

      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`,
      );
    }

    load();
  }, [load]);

  const handleConnect = async () => {
    if (state?.credential_source === 'marketplace') {
      setMessage({
        type: 'error',
        text: 'Этот портал подключён через Маркетплейс Битрикс24. Переустановите приложение «Связка» непосредственно в Битрикс24.',
      });
      return;
    }

    const cleanPortal = portal.trim();
    const cleanClientId = clientId.trim();
    const cleanClientSecret = clientSecret.trim();

    if (!cleanPortal) {
      setMessage({ type: 'error', text: 'Укажите адрес портала Bitrix24.' });
      return;
    }
    if (!cleanClientId) {
      setMessage({ type: 'error', text: 'Укажите client_id локального приложения Bitrix24.' });
      return;
    }
    if (!cleanClientSecret && !state?.client_secret_configured) {
      setMessage({ type: 'error', text: 'Укажите client_secret локального приложения Bitrix24.' });
      return;
    }

    setConnecting(true);
    setMessage(null);

    try {
      await saveBitrixConfig({
        portal_domain: cleanPortal,
        client_id: cleanClientId,
        ...(cleanClientSecret ? { client_secret: cleanClientSecret } : {}),
      });
      setClientSecret('');
      const result = await startBitrixOAuth(cleanPortal);

      if (!result.authorize_url) {
        throw new Error('Сервер не вернул ссылку авторизации Bitrix24');
      }

      window.location.assign(result.authorize_url);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.message || 'Не удалось начать авторизацию Bitrix24',
      });
      setConnecting(false);
    }
  };

  const handleCreateLine = async () => {
    const name = newLineName.trim();

    if (name.length < 2) {
      setMessage({
        type: 'error',
        text: 'Укажите название Открытой линии.',
      });
      return;
    }

    setCreatingLine(true);
    setMessage(null);

    try {
      const result = await createBitrixOpenLine(name);

      const created = result.line;

      const lineResult = await getBitrixOpenLines();

      setLines(lineResult.lines || []);
      setNewLineName('');

      setMessage({
        type: 'success',
        text: created?.name
          ? `Открытая линия «${created.name}» создана.`
          : 'Открытая линия создана.',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.message || 'Не удалось создать Открытую линию',
      });
    } finally {
      setCreatingLine(false);
    }
  };

  const setChannelLine = (channelId, lineId) => {
    setChannels((current) => current.map((channel) => (
      channel.channel_id === channelId
        ? {
            ...channel,
            line_id: lineId || null,
          }
        : channel
    )));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await saveBitrixBindings(
        channels.map((channel) => ({
          channel_id: channel.channel_id,
          line_id: channel.line_id || null,
        })),
      );

      await load();

      setMessage({
        type: 'success',
        text: 'Подключения к Bitrix24 сохранены.',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.message || 'Не удалось сохранить подключения',
      });
    } finally {
      setSaving(false);
    }
  };


  const parseOperatorIds = (rawValue) => {
    const values = String(rawValue || '')
      .split(/[,\s;]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    const invalid = values.find(
      (value) => !/^[1-9]\d*$/.test(value),
    );

    if (invalid) {
      throw new Error(
        `Некорректный ID сотрудника Bitrix24: ${invalid}`,
      );
    }

    return [...new Set(values)];
  };

  const handleSaveRouting = async () => {
    setRoutingSaving(true);
    setMessage(null);

    try {
      const items = routingItems.map((item) => ({
        channel_id: item.channel_id,
        operator_external_ids: parseOperatorIds(
          routingDraft[item.channel_id],
        ),
      }));

      await saveBitrixOperatorRouting(items);
      await load();

      setMessage({
        type: 'success',
        text: 'Доступ сотрудников к личным аккаунтам сохранён.',
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.message
          || 'Не удалось сохранить доступ сотрудников',
      });
    } finally {
      setRoutingSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <h1>Интеграция с Bitrix24</h1>
        <div className="card">Загрузка…</div>
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ marginBottom: 6 }}>Интеграция с Bitrix24</h1>
        <div style={{ color: '#718096', fontSize: 14 }}>
          Подключение мессенджеров к Открытым линиям Bitrix24.
        </div>
      </div>

      {message && (
        <div
          className="card"
          style={{
            borderLeft: `4px solid ${
              message.type === 'success'
                ? 'var(--success)'
                : 'var(--danger)'
            }`,
          }}
        >
          <strong>
            {message.type === 'success' ? 'Готово' : 'Ошибка'}
          </strong>
          <div style={{ marginTop: 4, fontSize: 14 }}>
            {message.text}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h3>Bitrix24</h3>

          <span
            className={`badge ${
              state?.connected ? 'badge-ok' : 'badge-error'
            }`}
          >
            {state?.connected
              ? 'Подключён'
              : state?.needs_reauth
                ? 'Нужно переподключить'
                : 'Не подключён'}
          </span>
        </div>

        {!state?.connected ? (
          state?.credential_source === 'marketplace' ? (
            <div
              style={{
                padding: 16,
                border: '1px solid #d9e2ec',
                borderRadius: 8,
              }}
            >
              <strong>
                Связка установлена через Маркетплейс Битрикс24
              </strong>

              <div
                style={{
                  marginTop: 8,
                  color: '#718096',
                  fontSize: 14,
                }}
              >
                Для повторной авторизации переустановите приложение
                «Связка» непосредственно в Битрикс24. Код приложения
                и ключ приложения здесь не требуются.
              </div>
            </div>
          ) : (
          <>
            <div style={{ marginBottom: 16, color: '#718096', fontSize: 14 }}>
              Авторизуйте портал Bitrix24. Пароль от Bitrix24 в «Связку»
              не передаётся.
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(260px, 1fr) auto',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label>Адрес портала</label>
                <input
                  value={portal}
                  onChange={(event) => setPortal(event.target.value)}
                  placeholder="crm.example.ru"
                  disabled={connecting}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Код приложения (client_id)</label>
                <input
                  value={clientId}
                  onChange={(event) => setClientId(event.target.value)}
                  placeholder="local.xxxxxxxxxx.xxxxxxxx"
                  autoComplete="off"
                  disabled={connecting}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Ключ приложения (client_secret)</label>
                <input
                  type="password"
                  value={clientSecret}
                  onChange={(event) => setClientSecret(event.target.value)}
                  placeholder={state?.client_secret_configured ? 'Секрет сохранён — оставьте пустым, чтобы не менять' : 'Введите client_secret'}
                  autoComplete="new-password"
                  disabled={connecting}
                />
              </div>

              <button
                className="btn-primary"
                onClick={handleConnect}
                disabled={
                  connecting
                  || !portal.trim()
                  || !clientId.trim()
                  || (!clientSecret.trim() && !state?.client_secret_configured)
                }
              >
                {connecting
                  ? 'Переходим в Bitrix24…'
                  : state?.needs_reauth
                    ? 'Переподключить Bitrix24'
                    : 'Подключить Bitrix24'}
              </button>
            </div>
          </>
          )
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div>
              <div style={{ color: '#718096', fontSize: 13 }}>
                Портал
              </div>
              <strong>{state.portal_domain}</strong>
            </div>

            <button
              className="btn-ghost"
              onClick={handleConnect}
              disabled={
                connecting
                || state?.credential_source === 'marketplace'
              }
            >
              {state?.credential_source === 'marketplace'
                ? 'Управляется через Маркетплейс'
                : 'Переподключить'}
            </button>
          </div>
        )}
      </div>

      {state?.connected && (
        <>
          <div className="card">
            <div className="card-header">
              <div>
                <h3 style={{ marginBottom: 4 }}>
                  Открытые линии
                </h3>
                <div style={{ color: '#718096', fontSize: 13 }}>
                  Используйте существующую линию или создайте новую.
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(260px, 1fr) auto',
                gap: 12,
                alignItems: 'end',
              }}
            >
              <div className="form-group" style={{ margin: 0 }}>
                <label>Название новой линии</label>
                <input
                  value={newLineName}
                  onChange={(event) => setNewLineName(event.target.value)}
                  placeholder="Например, Связка — Telegram"
                  disabled={creatingLine}
                />
              </div>

              <button
                className="btn-primary"
                onClick={handleCreateLine}
                disabled={creatingLine || newLineName.trim().length < 2}
              >
                {creatingLine ? 'Создаём…' : 'Создать линию'}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <h3 style={{ marginBottom: 4 }}>
                  Подключения мессенджеров
                </h3>
                <div style={{ color: '#718096', fontSize: 13 }}>
                  Выберите Открытую линию для каждого аккаунта.
                </div>
              </div>
            </div>

            {channels.length === 0 ? (
              <div style={{ color: '#718096', fontSize: 14 }}>
                Нет активных подключений мессенджеров.
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gap: 12 }}>
                  {channels.map((channel) => (
                    <div
                      key={channel.channel_id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'minmax(220px, 1fr) minmax(260px, 1fr)',
                        gap: 16,
                        alignItems: 'center',
                        padding: '14px 0',
                        borderBottom: '1px solid #edf2f7',
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>
                          {channel.name || channelLabel(channel)}
                        </div>
                        <div
                          style={{
                            color: '#718096',
                            fontSize: 13,
                            marginTop: 3,
                          }}
                        >
                          {channelLabel(channel)}
                        </div>
                      </div>

                      <div className="form-group" style={{ margin: 0 }}>
                        <select
                          value={channel.line_id || ''}
                          onChange={(event) => setChannelLine(
                            channel.channel_id,
                            event.target.value,
                          )}
                          disabled={saving}
                        >
                          <option value="">
                            Не передавать в Bitrix24
                          </option>

                          {lines.map((line) => (
                            <option
                              key={line.id}
                              value={line.id}
                            >
                              {line.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 20,
                  }}
                >
                  <button
                    className="btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Сохраняем…' : 'Сохранить подключения'}
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <h3 style={{ marginBottom: 4 }}>
                  Доступ сотрудников к личным аккаунтам
                </h3>

                <div
                  style={{
                    color: '#718096',
                    fontSize: 13,
                  }}
                >
                  Укажите, какие сотрудники Bitrix24 могут
                  видеть и использовать каждый личный аккаунт.
                </div>
              </div>
            </div>

            {routingItems.length === 0 ? (
              <div
                style={{
                  color: '#718096',
                  fontSize: 14,
                }}
              >
                Сначала подключите личные аккаунты
                к Открытой линии выше.
              </div>
            ) : (
              <>
                <div
                  style={{
                    display: 'grid',
                    gap: 12,
                  }}
                >
                  {routingItems.map((item) => (
                    <div
                      key={item.channel_id}
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'minmax(220px, 1fr) minmax(300px, 1fr)',
                        gap: 16,
                        alignItems: 'center',
                        padding: '14px 0',
                        borderBottom:
                          '1px solid #edf2f7',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontWeight: 600,
                          }}
                        >
                          {item.phone || 'Номер не указан'}
                        </div>

                        <div
                          style={{
                            color: '#718096',
                            fontSize: 13,
                            marginTop: 3,
                          }}
                        >
                          {channelLabel(item)}
                          {' · '}
                          Открытая линия {item.line_id || 'не назначена'}
                        </div>
                      </div>

                      <CompactOperatorAccess
                        value={routingDraft[item.channel_id] ?? ''}
                        users={bitrixEmployees}
                        loading={employeesLoading}
                        error={employeesError}
                        disabled={routingSaving}
                        onChange={(value) => setRoutingDraft(
                          (current) => ({
                            ...current,
                            [item.channel_id]: value,
                          }),
                        )}
                      />
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    marginTop: 16,
                    padding: 12,
                    borderRadius: 8,
                    background: '#f7fafc',
                    color: '#4a5568',
                    fontSize: 13,
                  }}
                >
                  Назначенный, но не авторизованный аккаунт
                  остаётся видимым сотруднику как требующий
                  подключения и недоступен для отправки.
                </div>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 20,
                  }}
                >
                  <button
                    className="btn-primary"
                    onClick={handleSaveRouting}
                    disabled={routingSaving}
                  >
                    {routingSaving
                      ? 'Сохраняем…'
                      : 'Сохранить доступ сотрудников'}
                  </button>
                </div>
              </>
            )}
          </div>

        </>
      )}
    </>
  );
}
